import { useState } from "react";
import { loginWithApple, loginWithGoogle, requestLoginCode, submitLoginCode } from "../services/session.service.js";
import { SocialSignIn } from "../components/SocialSignIn.comp.js";
import type { AppleCredential } from "../services/socialAuth.service.js";
import type { SocialAuthResult } from "../services/session.service.js";

/**
 * Two-stage login: email → emailed code → signed in. Mirrors the mobile no-auth login stack
 * (/no-auth/login/step-1 → step-2) and calls the same two endpoints.
 *
 * In any non-prod environment the server writes a fixed verification code
 * (`NON_PROD_ONLY.DEFAULT_CODE`), which is what makes the live Playwright spec deterministic.
 */

type Stage = "email" | "code";

/** Good enough to catch a typo before spending a round trip; the server is the real authority. */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function LoginPage(p: { onCreateAccount: (opts?: { notice?: string }) => void }) {
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!looksLikeEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setIsBusy(true);
    try {
      const result = await requestLoginCode(email.trim());
      if (result.ok) {
        setStage("code");
      } else {
        setError(result.message);
      }
    } catch {
      setError("We couldn't reach Roo. Check your connection and try again.");
    } finally {
      setIsBusy(false);
    }
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!code.trim()) {
      setError("Enter the code we emailed you.");
      return;
    }
    setIsBusy(true);
    try {
      // On success this flips session state, and App swaps this page out — no navigation here.
      const result = await submitLoginCode(email.trim(), code.trim());
      if (!result.ok) setError(result.message);
    } catch {
      setError("We couldn't reach Roo. Check your connection and try again.");
    } finally {
      setIsBusy(false);
    }
  }

  /**
   * A provider credential, exchanged for a session. `UNABLE_TO_FIND_USER` is a routing outcome, not
   * an error: we send the user to SignUp, where the same button creates the account. This mirrors
   * mobile's `LoginWithGoogle` → `onNeedsSignup`, so web and mobile stay on one auth model — a "Log
   * in" button that silently provisions accounts would be a product decision, not wiring.
   *
   * Both providers share this: which one produced the token is the service's business, and the
   * login-vs-signup decision is identical either way.
   */
  async function onSocialLogin(attempt: () => Promise<SocialAuthResult>) {
    setError(null);
    setIsBusy(true);
    try {
      const result = await attempt();
      if (result.ok) return; // Session state flipped; App swaps this page out.
      if (result.needsSignUp) {
        p.onCreateAccount({ notice: result.message });
        return;
      }
      setError(result.message);
    } catch {
      setError("We couldn't reach Roo. Check your connection and try again.");
    } finally {
      setIsBusy(false);
    }
  }

  const onGoogleIdToken = (identityToken: string) => onSocialLogin(() => loginWithGoogle(identityToken));
  const onAppleCredential = (credential: AppleCredential) =>
    onSocialLogin(() => loginWithApple(credential.identityToken));

  function onStartOver() {
    setStage("email");
    setCode("");
    setError(null);
  }

  return (
    <main className="card" data-testid="login-page">
      <h1 className="logo">roo</h1>

      {stage === "email" ? (
        <form onSubmit={onSubmitEmail} data-testid="login-email-form" noValidate>
          <h2>Log in</h2>
          <p className="hint">We'll email you a code — no password to remember.</p>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            data-testid="login-email-input"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isBusy}
          />
          <button type="submit" data-testid="login-email-submit" disabled={isBusy}>
            {isBusy ? "Sending…" : "Send code"}
          </button>
          <button
            type="button"
            className="link"
            data-testid="login-to-signup"
            onClick={() => p.onCreateAccount()}
            disabled={isBusy}
          >
            New to Roo? Create an account
          </button>
        </form>
      ) : null}

      {/* Only on the email stage: once a code has been sent, the user is mid-flow on one identity
          and offering a second way in reads as an escape hatch from a step they can't finish. */}
      {stage === "email" ? (
        <SocialSignIn
          testIdPrefix="login"
          onGoogleIdToken={onGoogleIdToken}
          onAppleCredential={onAppleCredential}
          disabled={isBusy}
        />
      ) : null}

      {stage === "code" ? (
        <form onSubmit={onSubmitCode} data-testid="login-code-form" noValidate>
          <h2>Enter your code</h2>
          <p className="hint">
            Sent to <strong data-testid="login-code-email">{email.trim()}</strong>
          </p>
          <label htmlFor="code">Verification code</label>
          <input
            id="code"
            data-testid="login-code-input"
            // `text` + numeric inputMode: the code is alphanumeric in prod, so `type=number`
            // would silently reject valid codes.
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isBusy}
          />
          <button type="submit" data-testid="login-code-submit" disabled={isBusy}>
            {isBusy ? "Signing in…" : "Log in"}
          </button>
          <button type="button" className="link" data-testid="login-start-over" onClick={onStartOver} disabled={isBusy}>
            Use a different email
          </button>
        </form>
      ) : null}

      {error ? (
        <p className="error" role="alert" data-testid="login-error">
          {error}
        </p>
      ) : null}
    </main>
  );
}
